import { uniqueId } from 'lodash'
import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react'
import ReactPlayer from 'react-player'
import { usePrevious } from 'react-use'
import DotsLoader from 'shared/components/loaders/dots'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM } from '../constants'
import { useCreationSourcePreviewUrl } from '../hooks/use-creation-source-preview-url'
import { CreationToolsContext } from '../manager'
import { CreationToolSourceFile } from '../types'
import { ToolButton } from './buttons'
import { PauseIcon as PauseIconComponent, PlayIcon as PlayIconComponent, SoundOffIcon, SoundOnIcon } from './icons'
import { SourceEditorCrop } from './source-editor/types'

const Container = styled.div`
    background: #000;
    position: relative;
    width: 100%;
`

const PlayerWrapper = styled.div`
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    overflow: hidden;
    position: relative;

    iframe {
        display: block;
        height: 200%;
        transform: translateY(-25%);
        width: 100%;
    }

    video {
        display: block;
        object-fit: cover;
        width: 100%;
    }
`

const Button = styled(ToolButton)`
    align-items: center;
    bottom: 10px;
    display: flex;
    height: 30px;
    justify-content: center;
    position: absolute;
    width: 30px;
`

const VolumeButton = styled(Button)`
    right: 10px;

    svg {
        height: 18px;
    }
`

const PauseButton = styled(Button)`
    left: 10px;
`

const PlayIcon = styled(PlayIconComponent)`
    height: 24px;
`

const PauseIcon = styled(PauseIconComponent)`
    height: 16px;
`

type Props = {
    className?: string
    children?: ReactNode
    source: CreationToolSourceFile
    startTime?: number
    endTime?: number
    hideButtons?: boolean
    width: number
    crop?: SourceEditorCrop
    onTimeUpdate?: (currentTime: number) => void
}

const CreationToolsVideoPlayer = ({
    className,
    children,
    source,
    startTime,
    endTime,
    width,
    crop,
    hideButtons = false,
    onTimeUpdate,
}: Props) => {
    const { currentAudioId, setCurrentAudioId } = useContext(CreationToolsContext)
    const playerRef = useRef<any>(null)
    const playerIdRef = useRef<string>(uniqueId('videoplayer'))
    const pauseTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null)
    const prevStartTime = usePrevious(startTime)
    const prevEndTime = usePrevious(endTime)
    const [isMuted, setIsMuted] = useState<boolean>(true)
    const [isPlaying, setIsPlaying] = useState<boolean>(true)
    const videoPreviewUrl = useCreationSourcePreviewUrl(source.source)

    // calculate crop positions
    const aspectRatio = crop ? crop.width / crop.height : source.width / source.height
    const height = Math.round(width / aspectRatio)

    const playerWidth = crop ? Math.round(width * (source.width / crop.width)) : width
    const playerHeight = Math.round(playerWidth / (source.width / source.height))
    const playerStyles = crop
        ? {
              left: Math.round(playerWidth * -(crop.x / source.width)),
              top: Math.round(playerHeight * -(crop.y / source.height)),
              width: playerWidth,
              height: playerHeight,
          }
        : undefined

    useEffect(() => {
        // if an audio source id is set and it's not this player, mute it
        if (currentAudioId !== playerIdRef.current) {
            setIsMuted(true)
        }
    }, [currentAudioId])

    useEffect(() => {
        if (hideButtons) {
            setIsMuted(true)
        }
    }, [hideButtons, setIsMuted])

    useEffect(() => {
        if (typeof startTime === 'undefined' || typeof endTime === 'undefined') {
            return
        }

        if (startTime !== prevStartTime) {
            jumpToTime(startTime)
        } else if (endTime !== prevEndTime) {
            jumpToTime(endTime - 0.1)
        }
    }, [startTime, prevStartTime, endTime, prevEndTime])

    const jumpToTime = (time: number) => {
        if (pauseTimeoutRef.current) {
            clearTimeout(pauseTimeoutRef.current)
        }

        if (playerRef.current) {
            setIsPlaying(false)
            playerRef.current.seekTo(time, 'seconds')
            pauseTimeoutRef.current = setTimeout(() => {
                setIsPlaying(true)
            }, 1000)
        }
    }

    const onProgress = () => {
        const player = playerRef.current
        const ignoreProgress = !endTime && !onTimeUpdate

        if (!player || ignoreProgress) return

        const currentTime = player.getCurrentTime()
        onTimeUpdate && onTimeUpdate(currentTime)

        // manual loop
        if ((startTime && currentTime < startTime) || (endTime && currentTime >= endTime)) {
            player.seekTo(startTime, 'seconds')
        }
    }

    const onMuteToggle = () => {
        const isMuting = !isMuted
        setIsMuted(isMuting)
        if (!isMuting) {
            setCurrentAudioId(playerIdRef.current)
        }
    }

    return !videoPreviewUrl ? (
        <DotsLoader />
    ) : (
        <Container className={className} style={{ height, width }}>
            <PlayerWrapper style={{ height, width }}>
                <PlayerWrapper style={playerStyles}>
                    <ReactPlayer
                        {...{
                            ref: playerRef,
                            url: videoPreviewUrl,
                            muted: isMuted,
                            playing: isPlaying,
                            loop: true,
                            playsinline: true,
                            progressInterval: 50,
                            height: playerHeight,
                            width: playerWidth,
                            onProgress,
                        }}
                    />
                </PlayerWrapper>
            </PlayerWrapper>
            {children}
            {!hideButtons && (
                <>
                    {source.hasAudio && (
                        <VolumeButton onClick={onMuteToggle}>
                            {isMuted ? <SoundOffIcon /> : <SoundOnIcon />}
                        </VolumeButton>
                    )}
                    <PauseButton onClick={() => setIsPlaying(!isPlaying)}>
                        {isPlaying ? <PauseIcon /> : <PlayIcon />}
                    </PauseButton>
                </>
            )}
        </Container>
    )
}

export default CreationToolsVideoPlayer
